import apiClient from './index';

/**
 * Format SQL clauses by adding spaces around them to prevent parsing bugs
 * @param {string} text - The text to format
 * @returns {string} - The formatted text
 */
const formatSQLClauses = (text) => {
  if (!text) return text;
  
  // SQL clauses that need spacing
  const sqlClauses = ['TOP', 'FROM', 'WHERE', 'SELECT', 'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', "AND", "BETWEEN", "IN", "LIKE", "OR", "NOT", "IS NULL", "IS NOT NULL"];
  
  let formattedText = text;
  
  sqlClauses.forEach(clause => {
    // Add space before clause (if not already there)
    const beforePattern = new RegExp(`(\\S)(${clause})`, 'gi');
    formattedText = formattedText.replace(beforePattern, '$1 $2');
    
    // Add space after clause (if not already there)
    const afterPattern = new RegExp(`(${clause})(\\S)`, 'gi');
    formattedText = formattedText.replace(afterPattern, '$1 $2');
  });
  
  // Clean up multiple spaces
  formattedText = formattedText.replace(/\s+/g, ' ');
  
  return formattedText;
};

/**
 * Check the status of the LLM service
 * @returns {Promise<Object>} - Status information
 */
export const getLLMStatus = async () => {
  try {
    const response = await apiClient.get('/adss/v1/llm/status');
    return response.data;
  } catch (error) {
    console.error('Error getting LLM status:', error);
    throw error;
  }
};

/**
 * Generate a query from a natural language prompt
 * @param {string} prompt - The natural language prompt
 * @param {string|null} socketId - Optional socket session ID for real-time updates (legacy)
 * @returns {Promise<string>} - The generated query
 */
export const generateQuery = async (prompt, socketId = null) => {
  try {
    // Prepare headers with socket ID if available
    const headers = {};
    if (socketId) {
      console.log(`Including socket ID in request: ${socketId}`);
      headers['x-socket-id'] = socketId;
    }
    
    // Make request with socket ID in headers if available
    const response = await apiClient.post('/adss/v1/llm/generate-query', 
      { prompt }, 
      { headers }
    );
    
    return response.data.query;
  } catch (error) {
    console.error('Error generating query:', error);
    throw error;
  }
};

/**
 * Generate a query from a natural language prompt with streaming response (using EventSource)
 * @param {string} prompt - The natural language prompt
 * @param {number} maxTokens - Maximum tokens to generate
 * @param {function} onUpdate - Callback for streaming updates
 * @param {function} onError - Callback for errors
 * @returns {Promise<string>} - The generated query
 */
export const generateQueryStreamEventSource = async (prompt, maxTokens = 256, onUpdate, onError) => {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      prompt,
      max_tokens: maxTokens.toString(),
    });
    
    // Add auth token to URL params since EventSource doesn't support custom headers
    if (token) {
      params.append('token', token);
    }
    
    const url = `/adss/v1/llm/generate-query-stream?${params}`;
    const eventSource = new EventSource(url);
    
    let accumulatedText = '';
    let finalQuery = '';
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.error) {
          onError(data.error);
          eventSource.close();
          reject(new Error(data.error));
          return;
        }
        
        if (data.status) {
          onUpdate({ type: 'status', content: data.status });
        }
        
        if (data.chunk && !data.done) {
          accumulatedText += data.chunk;
          // Apply SQL formatting to accumulated text
          const formattedText = formatSQLClauses(accumulatedText);
          onUpdate({ type: 'chunk', content: data.chunk, accumulated: formattedText });
        }
        
        if (data.query) {
          finalQuery = formatSQLClauses(data.query);
          onUpdate({ type: 'complete', content: finalQuery });
        }
        
        if (data.done) {
          eventSource.close();
          resolve(finalQuery);
        }
      } catch (e) {
        console.error('Error parsing EventSource data:', e);
        onError('Error parsing response');
        eventSource.close();
        reject(e);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
      onError('Connection error');
      eventSource.close();
      reject(error);
    };
  });
};


export default {
  getLLMStatus,
  generateQuery,
  generateQueryStreamEventSource,
};